import React, { useEffect } from "react";
import feather from "feather-icons";

const Whitepaper = () => {
  useEffect(() => {
    setTimeout(() => feather.replace(), 200);
  }, []);

  return (
    <div className="text-white min-h-screen container mx-auto px-4 py-16 max-w-6xl">
      {/* === HEADER === */}
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-neonPink to-neonBlue">
          NEXORA Whitepaper
        </h1>
        <p className="text-xl max-w-3xl mx-auto text-gray-300">
          Building a decentralized movement that empowers youth through education, culture, innovation, and equitable access to opportunity.
        </p>
      </div>

      <div className="bg-gray-900/80 backdrop-blur-lg rounded-xl p-12 border border-neonPurple/40 shadow-lg shadow-neonPurple/10 space-y-16">
        {/* === 1. INTRODUCTION === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPink">1. Introduction</h2>
          <p className="mb-4 text-lg">
            The <strong>NEXORA Token</strong> represents a new generation of decentralized empowerment — 
            a digital asset designed to finance, reward, and govern social, educational, and cultural innovation. 
            It bridges blockchain technology with real-world impact, ensuring transparency, inclusion, and accountability.
          </p>
          <p className="text-lg">
            NEXORA stands at the intersection of education, culture, and technology, empowering youth to 
            actively participate in governance, skill-building, and civic engagement.
          </p>
        </section>

        {/* === 2. VISION & MISSION === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonBlue">2. Vision & Mission</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 border border-neonPink/30 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-neonPink">Vision</h3>
              <p>
                To create a connected, educated, and empowered generation 
                that drives social transformation through decentralized tools and collective governance.
              </p>
            </div>
            <div className="p-6 border border-neonBlue/30 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-neonBlue">Mission</h3>
              <p>
                To use blockchain as a force for good — funding education, culture, and social programs 
                while ensuring that every NEXORA holder has a voice in shaping the ecosystem’s future.
              </p>
            </div>
          </div>
        </section>

        {/* === 3. PROBLEM STATEMENT === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPurple">3. Problem Statement</h2>
          <ul className="list-disc list-inside space-y-3 text-lg">
            <li>Unequal access to education and digital opportunities</li>
            <li>Limited youth involvement in governance and civic participation</li>
            <li>Insufficient funding for grassroots social and cultural initiatives</li>
            <li>Lack of transparent systems to measure and reward social impact</li>
          </ul>
        </section>

        {/* === 4. NEXORA SOLUTION === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPink">4. NEXORA Solution</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              ["NEXORA Token", "A utility and governance token enabling funding, participation, and rewards across the ecosystem.", "neonBlue"],
              ["NEXORA Platform", "A decentralized Web3 hub connecting youth to educational, cultural, and social opportunities.", "neonPurple"],
              ["Staking & Rewards", "A fair reward system for learners, mentors, and contributors within the community.", "neonPink"],
              ["Participatory Governance", "NEXORA DAO enables holders to propose, vote, and shape real-world impact projects.", "neonBlue"],
            ].map(([title, desc, color]) => (
              <div
                key={title}
                className={`p-6 border border-${color}/30 rounded-lg hover:shadow-md hover:shadow-${color}/20 transition-all`}
              >
                <h3 className={`text-xl font-bold mb-3 text-${color}`}>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* === 5. TOKENOMICS === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonBlue">5. Tokenomics</h2>
          <p className="text-lg mb-6 text-gray-300">
            NEXORA Token has a total supply of <strong>1,000,000,000 NEXORA</strong>, 
            distributed strategically to ensure fairness, sustainability, and long-term value creation.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <ul className="list-disc list-inside space-y-2">
                <li><span className="text-neonPink font-bold">30%</span> — Public Sale: 10% TGE, 3-month cliff, 15 months linear</li>
                <li><span className="text-neonBlue font-bold">15%</span> — Team: 12-month cliff, 24 months linear</li>
                <li><span className="text-neonPurple font-bold">5%</span> — Advisors: 6-month cliff, 18 months linear</li>
                <li><span className="text-teal-400 font-bold">25%</span> — Community: Monthly distribution over 36 months</li>
                <li><span className="text-yellow-400 font-bold">12%</span> — Ecosystem: 10% TGE, 36 months linear</li>
                <li><span className="text-pink-400 font-bold">8%</span> — Liquidity: 100% TGE, locked for 12 months</li>
                <li><span className="text-orange-400 font-bold">5%</span> — DAO Reserve: Controlled by governance for strategic initiatives</li>
              </ul>
            </div>
            <div className="flex justify-center">
              <img
                src="https://placehold.co/400x300/0f172a/ffffff?text=Token+Allocation+Chart"
                alt="Token Allocation Chart"
                className="rounded-lg border border-neonPurple/30"
              />
            </div>
          </div>
        </section>

        {/* === 6. ROADMAP === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPurple">6. Roadmap</h2>
          <div className="relative pl-8 border-l-2 border-neonPink">
            {[
              ["Q1", "Smart contract deployment, community building, and NEXORA token launch."],
              ["Q2", "Development of the NEXORA Web3 platform and education DAO modules."],
              ["Q3", "Launch of staking programs, cultural partnerships, and social campaigns."],
              ["Q4", "Expansion into international collaborations, ecosystem scaling, and DAO treasury activation."],
            ].map(([q, desc], i) => (
              <div key={q} className="mb-8 relative">
                <div
                  className={`absolute -left-10 w-6 h-6 rounded-full ${
                    i % 2 === 0 ? "bg-neonPink" : "bg-neonBlue"
                  } border-4 border-darkBg`}
                ></div>
                <h3 className="text-xl font-bold text-neonBlue mb-2">{q}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* === 7. ECOSYSTEM === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPink">7. NEXORA Ecosystem</h2>
          <div className="text-center">
            <img
              src="https://placehold.co/600x350/0f172a/ffffff?text=NEXORA+Ecosystem+Diagram"
              alt="NEXORA Ecosystem"
              className="mx-auto rounded-lg border border-neonBlue/30"
            />
            <p className="mt-4 text-gray-400">
              NEXORA connects five core pillars: Education • Culture • Health • Civic Life • Justice — 
              all powered by decentralized participation and transparent funding.
            </p>
          </div>
        </section>

        {/* === 8. GOVERNANCE === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonBlue">8. Governance & Fund Usage</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["NEXORA DAO", "A decentralized governance model where each holder votes on ecosystem proposals.", "neonPurple"],
              ["Transparency", "All fund allocations and transactions are verifiable on-chain.", "neonPink"],
              ["Strategic Use", "Funds support education, innovation, cultural projects, and long-term partnerships.", "neonBlue"],
            ].map(([title, desc, color]) => (
              <div
                key={title}
                className={`p-6 border border-${color}/30 rounded-lg hover:shadow-md hover:shadow-${color}/20 transition-all`}
              >
                <h3 className={`text-xl font-bold mb-3 text-${color}`}>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* === 9. SECURITY === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPurple">9. Security & Audit</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <ul className="list-disc list-inside space-y-3">
              <li>Secure and verified ERC-20 smart contract</li>
              <li>Independent third-party audit before launch</li>
              <li>Anti-bot and anti-rug mechanisms for safe trading</li>
              <li>Liquidity lock and DAO-governed treasury transparency</li>
            </ul>
            <div className="flex items-center justify-center">
              <img
                src="https://placehold.co/400x300/0f172a/ffffff?text=Audit+Verification"
                alt="Security"
                className="rounded-lg border border-neonPink/30"
              />
            </div>
          </div>
        </section>

        {/* === 10. CONCLUSION === */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-neonPink">10. Conclusion</h2>
          <div className="p-6 border border-neonBlue/30 rounded-lg bg-gradient-to-br from-darkBg to-gray-900">
            <p className="text-lg">
              NEXORA is more than a token — it’s a social movement. 
              It unites technology, education, and purpose to empower a generation capable of shaping 
              a fairer, more inclusive, and sustainable digital future.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Whitepaper;
